/**
 * Secretary API client.
 */
import { apiGet, apiPost, buildApiUrl } from './client';
import { authActions } from '../state/auth.state';
import type {
  PaginatedEntrySummaryResponse,
  EntryDetailDto,
  PdfDownloadResponse,
  PdfStatus,
} from './types';

export interface SecretaryEntriesParams {
  page?: number;
  pageSize?: number;
  trialEventId?: string;
  status?: 'Draft' | 'Submitted' | 'All';
  pdfStatus?: PdfStatus | 'All';
  search?: string;
}

export const secretaryApi = {
  /**
   * Get paginated entries for secretary view.
   */
  getEntries: (params: SecretaryEntriesParams = {}) => {
    const searchParams = new URLSearchParams();
    if (params.page !== undefined) searchParams.set('page', params.page.toString());
    if (params.pageSize !== undefined) searchParams.set('pageSize', params.pageSize.toString());
    if (params.status && params.status !== 'All') searchParams.set('status', params.status);
    if (params.pdfStatus && params.pdfStatus !== 'All') searchParams.set('pdfStatus', params.pdfStatus);
    if (params.search) searchParams.set('search', params.search);

    const basePath = params.trialEventId
      ? `/admin/trial-events/${params.trialEventId}/entries`
      : '/admin/entries';
    const queryString = searchParams.toString();
    const url = queryString ? `${basePath}?${queryString}` : basePath;
    return apiGet<PaginatedEntrySummaryResponse>(url);
  },

  /**
   * Get detailed entry for secretary view (includes processing info).
   */
  getEntryDetail: (trialEventId: string, entryId: string) =>
    apiGet<EntryDetailDto>(`/admin/trial-events/${trialEventId}/entries/${entryId}`),

  /**
   * Get PDF download URL for an entry.
   */
  getPdfDownloadUrl: (trialEventId: string, entryId: string) =>
    apiGet<PdfDownloadResponse>(`/admin/trial-events/${trialEventId}/entries/${entryId}/pdf`),

  /**
   * Retry PDF generation for an entry.
   */
  retryPdf: (trialEventId: string, entryId: string) =>
    apiPost<void>(`/admin/trial-events/${trialEventId}/entries/${entryId}/pdf/retry`, {}),

  /**
   * Export entries to CSV or XLSX. Triggers a file download via blob URL.
   */
  exportEntries: async (trialEventId: string, format: 'csv' | 'xlsx'): Promise<void> => {
    const token = await authActions.getAccessToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(
      buildApiUrl(`/admin/trial-events/${trialEventId}/entries/export?format=${format}`),
      { method: 'GET', headers }
    );

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status}`);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `entries-export.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};
